/**
 * "Take me there" for answers about ONE existing offer: bring a suitable offer
 * on screen — the one already open when it qualifies, else the first matching
 * row of the offers list — and open it, open its ⋮ menu, or tick it.
 */
import { pressInOrder, waitFor } from "../dom";
import type { AskOffer } from "../types";

export type { AskOffer, AskOfferAction } from "../types";
export { OFFER_STATUSES, PAID_STATUSES, UNPAID_INVOICE_STATUSES } from "../types";

const ROWS = '[data-help="offers-table"] .ms-DetailsRow';
const STATUS = "[data-help-status]";
const HEADER = '[data-help="offer-header-info"][data-help-status]';
const PRODUCT = "[data-help-product]";
const LOAD_MS = 10000;
const SETTLE_MS = 400;
const MENU_MS = 1500;

function press(el: Element | null): boolean {
  if (!el) return false;
  const target = el.matches("a, button, [role=button], [data-selection-toggle]")
    ? el
    : el.querySelector("a, button, [role=button]") ?? el;
  (target as HTMLElement).click();
  return true;
}

/** Resolves true once the offer is on screen in the requested way. */
export async function bringOffer(offer: AskOffer, navigate: (to: string) => void): Promise<boolean> {
  const fits = (el: Element | null | undefined) => {
    const code = el?.querySelector(STATUS)?.getAttribute("data-help-status") ?? el?.getAttribute("data-help-status");
    const product =
      el?.querySelector(PRODUCT)?.getAttribute("data-help-product") ?? el?.getAttribute("data-help-product") ?? "";
    return (
      !!code &&
      (!offer.statuses || offer.statuses.includes(code)) &&
      (!offer.product || product.includes(offer.product))
    );
  };

  if (offer.action === "open" && /^\/offer\/[^/]+/.test(window.location.pathname)) {
    if (fits(document.querySelector(HEADER))) return true;
  }

  if (window.location.pathname !== "/") navigate("/");
  const statusedRows = () => Array.from(document.querySelectorAll<HTMLElement>(ROWS)).filter((r) => r.querySelector(STATUS));
  if (!(await waitFor(() => (statusedRows().length ? true : null), LOAD_MS))) return false;
  await new Promise((r) => window.setTimeout(r, SETTLE_MS));

  const rows = statusedRows().filter(fits);
  const needed = offer.action === "select" ? offer.count ?? 1 : 1;
  if (rows.length < needed) return false;

  switch (offer.action) {
    case "open": {
      const before = window.location.pathname;
      if (!press(rows[0].querySelector('[data-help="offers-offer-id-link"]'))) return false;
      const opened = await waitFor(
        () => (window.location.pathname !== before && document.querySelector(HEADER) ? true : null),
        LOAD_MS
      );
      return !!opened;
    }
    case "menu":
      return press(rows[0].querySelector('[data-help="offers-row-menu"]'));
    case "select":
      for (const row of rows.slice(0, needed)) {
        if (row.getAttribute("aria-selected") === "true") continue;
        if (!press(row.querySelector('[data-selection-toggle="true"]'))) return false;
      }
      return true;
  }
}

/**
 * Start a new offer the way a person does — offers list → "New Offer" → the
 * product. Navigating straight to "/offer?type=…" from an open offer keeps
 * that offer's state, so the guide never takes that shortcut.
 */
export async function startNewOffer(product: "mtpl" | "casco", navigate: (to: string) => void): Promise<boolean> {
  if (window.location.pathname !== "/") navigate("/");
  if (!(await waitFor(() => document.querySelector(ROWS), LOAD_MS))) return false;
  // the list settling (or the drawer closing and handing focus back) can close the menu again: retry
  let opened = false;
  for (let attempt = 0; attempt < 3 && !opened; attempt++) {
    await new Promise((r) => window.setTimeout(r, SETTLE_MS));
    opened = await pressInOrder(['[data-help="offers-new-menu"]', `[data-help="offers-new-${product}"]`], MENU_MS);
  }
  if (!opened) return false;
  return !!(await waitFor(
    () => (window.location.pathname === "/offer" && document.querySelector('[data-help="offer-tab-general"]') ? true : null),
    LOAD_MS
  ));
}
